/* eslint-disable */
import { getMenuAvailabilityNotifications } from '@/api/menuAvailability'
import { play as playChime, reset as resetChime } from '@/services/menuAlertSound'

/**
 * Menu availability alerts for the platform roles (ADMIN / AGENT / MANAGER).
 *
 * Fed by the menu:v1:availability Ably channel. Each event says one menu item (or serving)
 * was switched on or off. The backend pre-words `message`, so nothing here interprets the
 * payload beyond routing it. Unread is local only: the backend keeps no read state.
 */

const SEEN_LIMIT = 500
const TOAST_LIMIT = 5

// Module scope, not state: a Set is not reactive in Vue 2, and 500 ids have no business
// showing up in every devtools snapshot.
const seenNotificationIds = new Set()

const initialState = () => ({
    // True once connectRealtime found a menu channel. BRANCH gets none (null, not an error).
    enabled: false,
    // History page rows, newest first. Live events are prepended while the page is open.
    items: [],
    // Day total as reported by REST, bumped locally for events that land on the viewed day.
    count: 0,
    // The day the page is showing, as echoed by the server. Never computed client-side.
    viewedDay: '',
    unread: 0,
    toasts: [],
    pageOpen: false,
    loading: false,
    error: null,
    // Replayed by resyncAfterReconnect so a refetch keeps the operator's date and limit.
    lastQuery: {}
})

export default {
    namespaced: true,

    state: initialState,

    getters: {
        // The sidebar badge.
        unreadCount: state => state.unread
    },

    mutations: {
        setEnabled (state, value) { state.enabled = value },
        setPageOpen (state, open) {
            state.pageOpen = open
            // Opening the page is the act of reading: whatever was pending is now seen.
            if (open) state.unread = 0
        },
        setLoading (state, value) { state.loading = value },
        setError (state, value) { state.error = value },
        setHistory (state, { items, count, viewedDay }) {
            state.items = items
            state.count = count
            state.viewedDay = viewedDay
        },
        prependItem (state, item) {
            state.items.unshift(Object.assign({}, item, { isNew: true }))
        },
        incrementCount (state) { state.count += 1 },
        bumpUnread (state) { state.unread += 1 },
        pushToast (state, toast) {
            state.toasts.push(toast)
            if (state.toasts.length > TOAST_LIMIT) state.toasts.shift()
        },
        dismissToast (state, id) {
            state.toasts = state.toasts.filter(t => t.id !== id)
        },
        setLastQuery (state, query) { state.lastQuery = query },
        reset (state) {
            Object.assign(state, initialState())
        }
    },

    actions: {
        handleEvent ({ state, commit, rootState }, payload) {
            if (!payload || !payload.notification_id) return

            // Ably is at-least-once, so a redelivery is possible.
            if (seenNotificationIds.has(payload.notification_id)) return
            seenNotificationIds.add(payload.notification_id)
            if (seenNotificationIds.size > SEEN_LIMIT) {
                seenNotificationIds.delete(seenNotificationIds.values().next().value)
            }

            // Looking at that very day's list: the row appears in place, a toast on top of
            // the table would only repeat it.
            if (state.pageOpen && payload.day_id === state.viewedDay) {
                commit('prependItem', payload)
                commit('incrementCount')
                return
            }

            commit('bumpUnread')
            commit('pushToast', Object.assign({}, payload, { id: payload.notification_id }))

            // Shares the order alert's mute toggle rather than adding a second control.
            if (rootState.liveOrders.soundEnabled) playChime(payload.notification_id)
        },

        /**
         * `date` is passed through untouched (the page converts it to the backend's
         * dd-MM-yyyy). Omitting it means "today", which the server decides, so the client
         * never has to know the day boundary. Never rejects: the page reads `error`.
         */
        async fetchHistory ({ state, commit }, { date, limit } = {}) {
            commit('setLoading', true)
            commit('setError', null)
            const params = {}
            if (date) params.date = date
            if (limit) params.limit = limit
            commit('setLastQuery', params)
            try {
                const { data } = await getMenuAvailabilityNotifications(params)
                commit('setHistory', {
                    items: data.notifications || [],
                    count: data.count || 0,
                    viewedDay: data.date || ''
                })
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    // This role gets no menu alerts. Silent, not an error.
                    commit('setHistory', { items: [], count: 0, viewedDay: '' })
                } else {
                    commit('setError', 'Could not load menu alerts. Please retry.')
                }
            } finally {
                commit('setLoading', false)
            }
        },

        // Delivery is best-effort with no replay, so close the gap left by a network blip.
        // Off the page there is nothing to reconcile: the list is fetched on open anyway.
        resyncAfterReconnect ({ state, dispatch }) {
            if (state.pageOpen) return dispatch('fetchHistory', state.lastQuery)
        },

        reset ({ commit }) {
            seenNotificationIds.clear()
            resetChime()
            commit('reset')
        }
    }
}
